import { computed, ref, type Ref } from 'vue'

export type MileageValidationResult =
  | { type: 'none' }
  | { type: 'error', kmAtService: number }
  | { type: 'overAnnualCap', maxAnnual: number }
  | { type: 'implausibleHigh', impliedMonthly: number, impliedAnnual: number, canSetMax: boolean, maxAnnual: number }
  | { type: 'high', impliedMonthly: number, impliedAnnual: number, suggested: number }
  | { type: 'low', kmSince: number, months: number, suggested: number | null }

export interface UseMileageValidationOptions {
  kmAtService: Ref<number | undefined>
  currentKm: Ref<number | undefined>
  monthsSinceService: Ref<number | undefined>
  annualKm: Ref<number | undefined>
  maxAnnualAllowed: Ref<number | undefined>
  kmMax: Ref<number>
  thresholdPct?: Ref<number>
}

export function useMileageValidation(options: UseMileageValidationOptions) {
  const { kmAtService, currentKm, monthsSinceService, annualKm, maxAnnualAllowed, kmMax } = options
  const dismissed = ref(false)

  const round5k = (value: number) => Math.min(Math.max(Math.round(value / 5000) * 5000, 5000), kmMax.value)

  const result = computed<MileageValidationResult>(() => {
    const svc = kmAtService.value
    const cur = currentKm.value
    const months = monthsSinceService.value
    const chosen = annualKm.value
    const t = (options.thresholdPct?.value ?? 15) / 100

    if (svc != null && cur != null && cur < svc) {
      return { type: 'error', kmAtService: svc }
    }

    if (dismissed.value) return { type: 'none' }

    if (chosen != null && maxAnnualAllowed.value != null && chosen > maxAnnualAllowed.value) {
      return { type: 'overAnnualCap', maxAnnual: maxAnnualAllowed.value }
    }

    if (svc == null || cur == null || months == null || chosen == null) return { type: 'none' }

    const kmSince = cur - svc
    const impliedMonthly = kmSince / months
    const impliedAnnual = impliedMonthly * 12

    if (kmSince > 3000 && impliedAnnual > kmMax.value * 1.15) {
      return { type: 'implausibleHigh', impliedMonthly, impliedAnnual, canSetMax: chosen < kmMax.value, maxAnnual: kmMax.value }
    }

    if (kmSince > 3000 && impliedAnnual > chosen * (1 + t)) {
      const suggested = round5k(impliedAnnual)
      if (suggested > chosen) return { type: 'high', impliedMonthly, impliedAnnual, suggested }
    }

    if (months >= 2 && impliedAnnual < chosen * (1 - t)) {
      const suggested = round5k(impliedAnnual)
      return { type: 'low', kmSince, months, suggested: suggested < chosen ? suggested : null }
    }

    return { type: 'none' }
  })

  function dismiss() {
    dismissed.value = true
  }

  function resetDismiss() {
    dismissed.value = false
  }

  return { result, dismiss, resetDismiss }
}
